import * as React from "react";
import * as ReactDOM from "react-dom";
import * as utils from "../utils/utils";
import * as apiSettings from "../api/settings";
import { Loader, LoaderStyle } from "../components/loader";
import { Button, ButtonSize, ButtonType } from "../components/button";
import { Alert, AlertType } from "../components/alert";
import { FormGroup } from "../components/form";
import { InputSideButton, InputMask, InputSize } from "../components/input";
import { TextArea } from "../components/textArea";

interface IProperties
{
    mode: "LicenceNotFound" | "LicenceInvalid" | "LicenceExpired"
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Enthält einen Info Text der beim Loader dargestellt wird.
     */
    loaderText?: string,

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        licence_key?: string
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class LicencePage extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean = true;

    /**
     * Erstellt eine neue Instanz der Klasse @see Help.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: false,
            form: {},
            errors: {}
        }

        this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        };
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.licence_key !== nextState.form.licence_key)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0] === "" ? undefined : value[0];
            return state;
        });
    }

    /**
     * Registriert den eingetragenen Lizenzschlüssel.
     */
    private onSaveClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true,
                loaderText: "Der Lizenzschlüssel wird registriert"
            });

            const { form } = this.state;
            await apiSettings.putLicence(form.licence_key);
            location.reload();
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        var { mode } = this.props;
        var {
            isLoading,
            loaderText,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var infoText: string;
        if (mode === "LicenceNotFound")
            infoText = "Sie haben den Terminplaner noch nicht mit einem Lizenzschlüssel registriert. Damit Sie fortfahren können, registrieren Sie einen Lizenzschlüssel.";
        else if (mode === "LicenceExpired")
            infoText = "Ihre Lizenz ist abgelaufen. Damit Sie fortfahren können, registrieren Sie einen neuen Lizenzschlüssel.";
        else
            infoText = "Der registrierte Lizenzschlüssel ist ungültig. Damit Sie fortfahren können, registrieren Sie einen neuen Lizenzschlüssel.";

        return (
            <div className="licence">
                <div className="licence_content">
                    <div className="licence_product">
                        <img
                            className="licence_product_logo"
                            src="/img/logos/terminplaner_logo_white.png" />
                        <span className="licence_product_name">Terminplaner</span>
                    </div>
                    <div className="licence_form">
                        {isLoading && <Loader loaderStyle={LoaderStyle.style1} infoText={loaderText} />}
                        <Alert
                            alertType={alertType}
                            isVisible={alertMessage ? true : false}
                            isClosable={true}
                            onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                            {alertMessage}
                        </Alert>
                        <p className="licence_form_info">
                            {infoText}
                        </p>
                        <div className="licence_form_group">
                            <FormGroup>
                                <TextArea
                                    placeholder="Hier den Lizenzschlüssel eintragen den Sie von der Firma in-com erhalten haben"
                                    value={form.licence_key}
                                    size={InputSize.normal}
                                    autoFocus={true}
                                    rows={10}
                                    errorMessage={errors["LicenceKey"]}
                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "licence_key")} />
                            </FormGroup>
                        </div>
                        <div className="licence_form_action">
                            <Button
                                style={{
                                    width: "100%"
                                }}
                                text="Lizenzschlüssel speichern"
                                type={ButtonType.primary}
                                size={ButtonSize.normal}
                                isDisabled={this._isSaveButtonDisabled}
                                onClick={this.onSaveClick} />
                        </div>
                        <div className="licence_form_links">
                            <a
                                href="https://in-com.de/Kontakt"
                                target="_blank">
                                Sie haben keinen Lizenzschlüssel?
                        </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}